// =============================================================================
// R2 Mission Control — Transformed Home Page
// P0: Engine of the Day · Quick Launch Grid · Continue Where You Left Off
//     Heat Map · Live Metrics · What's New
// =============================================================================

import { Boxes, Building2, AlertTriangle, FolderKanban, Network, Cable, DoorOpen, Server,
  FolderSearch, Globe, X, EyeOff, TrendingUp, ChevronRight, Pin, Zap, Star,
  Brain, Keyboard, Mic, Film, Music, Map, Ruler, Layers, Cpu,
  Clock, ChevronLeft, Play, ArrowRight, Activity, Sparkles, Radio, FileText, Camera, Package, Bookmark,
  Settings2, ChevronUp, ChevronDown, Eye, EyeOff as EyeOffIcon, Maximize2, Minimize2, Check, RotateCcw, Wand2 } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDashboardStats, useDashboardActivity, type DashboardStats } from '../hooks/useDashboard.js';
import { Calendar } from '../components/ui/Calendar.js';
import { DualWorldClock } from '../components/dashboard/DualWorldClock.js';
import { WinsFeed } from '../components/dashboard/WinsFeed.js';
import { SystemOverviewDiagram } from '../components/dashboard/SystemOverviewDiagram.js';
import { HolographicEngineCore } from '../components/dashboard/HolographicEngineCore.js';
import { useAuthStore } from '../stores/auth.store.js';
import type { Route } from '../App.js';
import { useLicense } from '../hooks/useLicense.js';
import { RcddBadge } from '../components/branding/RcddBadge.js';
import { IntelligenceFeed } from '../components/dashboard/IntelligenceFeed.js';
import { SubmittalGenerator } from '../components/submittal/SubmittalGenerator.js';
import { MasterContextIngestor } from '../components/context/MasterContextIngestor.js';
import { DemoModeToggle } from '../components/dashboard/DemoModeToggle.js';
import { GlobalOperationalMap } from '../components/public/GlobalOperationalMap.js';
import { CommunityPartners } from '../components/community/CommunityPartners.js';
import { EliteUnlock } from '../components/elite/IdentityTransformation.js';
import { CelebrationBanner } from '../components/celebration/CelebrationBanner.js';
import { FlashlightTile } from '../components/nightOps/FlashlightTile.js';
import { useNightOpsStore } from '../stores/nightOps.store.js';
import { NightOpsPanel } from '../components/nightOps/NightOpsPanel.js';
import { useSessionResume } from '../stores/sessionResume.store.js';
import { useDashboardLayout, useWidget, useSortedWidgets, SIZE_SCALE, type WidgetSize } from '../stores/dashboardLayout.store.js';
import { toast } from '../lib/toast.js';
import { ingestStore } from '../stores/ingest.store.js';
import { QuickIngestDialog } from '../components/sie/QuickIngestDialog.js';
import { R2CommandCenter } from '../components/dashboard/R2CommandCenter.js';

const API = (import.meta as any).env.VITE_BACKEND_URL || '';

// ── Palette ────────────────────────────────────────────────────────────────────
const TEAL  = '#5eead4';
const AMBER = '#f59e0b';
const PANEL = 'rgba(255,255,255,0.03)';
const BDR   = 'rgba(255,255,255,0.07)';

// ── Night Ops tile row ─────────────────────────────────────────────────────────
function NightOpsTileRow() {
  const [showPanel, setShowPanel] = useState(false);
  return (
    <>
      <div data-night-exempt style={{ marginBottom: 16, marginTop: 8 }}>
        <FlashlightTile onOpenPanel={() => setShowPanel(true)} />
      </div>
      {showPanel && <NightOpsPanel onClose={() => setShowPanel(false)} />}
    </>
  );
}

// ── Parallax hook ──────────────────────────────────────────────────────────────
function useMouseParallax() {
  const targetRef  = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const rafRef     = useRef(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      targetRef.current = { x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 };
    };
    const tick = () => {
      const c = currentRef.current; const t = targetRef.current;
      c.x += (t.x - c.x) * 0.035; c.y += (t.y - c.y) * 0.035;
      setTilt({ x: c.x, y: c.y }); rafRef.current = requestAnimationFrame(tick);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => { window.removeEventListener('mousemove', onMove); cancelAnimationFrame(rafRef.current); };
  }, []);
  return tilt;
}

interface StatTileDef { id: string; label: string; field: keyof Omit<DashboardStats, 'generatedAt'>; icon: typeof Boxes; }
const STATS: StatTileDef[] = [
  { id: 'assets',        label: 'Devices',        field: 'assets',       icon: Boxes },
  { id: 'buildings',     label: 'Buildings',      field: 'buildings',    icon: Building2 },
  { id: 'racks',         label: 'Racks',          field: 'racks',        icon: Server },
  { id: 'telecomSpaces', label: 'Telecom Spaces', field: 'telecomSpaces',icon: DoorOpen },
  { id: 'ports',         label: 'Ports',          field: 'ports',        icon: Network },
  { id: 'connections',   label: 'Connections',    field: 'connections',  icon: Cable },
  { id: 'projects',      label: 'Projects',       field: 'projects',     icon: FolderKanban },
  { id: 'openClashes',   label: 'Open Clashes',   field: 'openClashes',  icon: AlertTriangle },
];
function fmt(n: number | undefined): string { return typeof n === 'number' ? n.toLocaleString() : '—'; }

// ═══════════════════════════════════════════════════════════════════════════════
// ENGINE OF THE DAY — Hero carousel cycling every 20s
// ═══════════════════════════════════════════════════════════════════════════════
const ENGINES = [
  { id: 'r2-brain',            label: 'R2 Brain',             subtitle: 'AI Assistant & Knowledge Engine',  icon: Brain,      color: '#a78bfa', glow: 'rgba(167,139,250,0.15)', tag: 'AI' },
  { id: 'r2-iie',              label: 'Input Intelligence',   subtitle: 'Universal Command & Shortcut Engine', icon: Keyboard, color: '#5eead4', glow: 'rgba(94,234,212,0.15)',  tag: 'NEW' },
  { id: 'r2-measurement-engine', label: 'Smart Measurement',  subtitle: 'Precision Dimensioning & QTO',     icon: Ruler,      color: '#f59e0b', glow: 'rgba(245,158,11,0.15)',  tag: 'BIM' },
  { id: 'r2-scse',             label: 'Strategy & Cinema',    subtitle: 'AI Scene Composer & Timeline',     icon: Film,       color: '#f472b6', glow: 'rgba(244,114,182,0.15)', tag: 'Creative' },
  { id: 'r2-voice-engine',     label: 'Voice Intelligence',   subtitle: 'AI Voice Commands & NLP',          icon: Mic,        color: '#34d399', glow: 'rgba(52,211,153,0.15)',  tag: 'AI' },
  { id: 'r2-aiqe',             label: 'Audio Intelligence',   subtitle: 'BPM Analysis & Auto-Mastering',   icon: Music,      color: '#fb923c', glow: 'rgba(251,146,60,0.15)',  tag: 'Audio' },
  { id: 'r2-old-earth-world',  label: 'Old Earth World',      subtitle: 'Interactive Globe & Civilizations',icon: Globe,      color: '#22d3ee', glow: 'rgba(34,211,238,0.15)',  tag: 'Explore' },
  { id: 'r2-3d-engine',        label: '3D Intelligence',      subtitle: 'Parametric BIM & 3D Viewer',      icon: Layers,     color: '#60a5fa', glow: 'rgba(96,165,250,0.15)',  tag: '3D' },
  { id: 'r2-wsie',             label: 'Workspace Notes',      subtitle: 'AI-Powered Sticky Intelligence',   icon: Bookmark,   color: '#fbbf24', glow: 'rgba(251,191,36,0.15)',  tag: 'Workspace' },
  { id: 'r2-dcre',             label: 'Disaster Engine',      subtitle: 'Seismic, Flood, Fire Simulation',  icon: AlertTriangle, color: '#f87171', glow: 'rgba(248,113,113,0.15)', tag: 'Safety' },
  { id: 'r2-mvie',             label: 'Music Video AI',       subtitle: 'Beat-Sync Storyboard Generation',  icon: Camera,     color: '#c084fc', glow: 'rgba(192,132,252,0.15)', tag: 'Creative' },
  { id: 'marketplace',         label: '3D Marketplace',       subtitle: 'Curated Models & Assets',          icon: Package,    color: '#4ade80', glow: 'rgba(74,222,128,0.15)',  tag: 'Store' },
];

function EngineOfTheDay({ onNavigate }: { onNavigate: (r: Route) => void }) {
  const [idx, setIdx]     = useState(0);
  const [fading, setFading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = useCallback((next: number) => {
    setFading(true);
    setTimeout(() => { setIdx((next + ENGINES.length) % ENGINES.length); setFading(false); }, 300);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => advance(idx + 1), 20000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [idx, advance]);

  const engine = ENGINES[idx];
  if (!engine) return null;
  const Icon   = engine.icon;

  return (
    <div data-testid="engine-of-the-day"
      style={{
        position: 'relative', overflow: 'hidden', borderRadius: 16,
        background: `linear-gradient(135deg, rgba(6,10,20,0.97) 0%, ${engine.glow} 100%)`,
        border: `1px solid ${engine.color}22`, padding: '28px 32px', marginBottom: 20,
        minHeight: 180, display: 'flex', alignItems: 'center', gap: 32,
        transition: 'border-color .4s',
      }}>
      {/* Animated glow bg */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: fading ? 0 : 0.07, transition: 'opacity .3s',
        background: `radial-gradient(ellipse at 20% 50%, ${engine.color} 0%, transparent 60%)` }} />

      {/* Icon */}
      <div style={{ flexShrink: 0, width: 88, height: 88, borderRadius: 20, display: 'grid', placeItems: 'center',
        background: `${engine.color}18`, border: `1.5px solid ${engine.color}44`,
        boxShadow: `0 0 30px ${engine.color}20`, opacity: fading ? 0 : 1, transition: 'opacity .3s, transform .3s',
        transform: fading ? 'scale(0.92)' : 'scale(1)',
      }}>
        <Icon size={38} color={engine.color} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, opacity: fading ? 0 : 1, transition: 'opacity .3s', transform: fading ? 'translateX(-12px)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: engine.color,
            background: `${engine.color}15`, padding: '2px 8px', borderRadius: 4 }}>{engine.tag}</span>
          <span style={{ fontSize: 10, color: '#334155', letterSpacing: '0.08em' }}>ENGINE OF THE DAY</span>
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', margin: '0 0 6px', letterSpacing: '-0.02em' }}>{engine.label}</h2>
        <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>{engine.subtitle}</p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
        <button data-testid="eod-launch-btn"
          onClick={() => onNavigate(engine.id as Route)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
            background: `${engine.color}18`, border: `1px solid ${engine.color}44`, color: engine.color,
            fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <Play size={13} /> Open Engine
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => advance(idx - 1)} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${BDR}`, background: PANEL, color: '#64748b', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><ChevronLeft size={14} /></button>
          <button onClick={() => advance(idx + 1)} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${BDR}`, background: PANEL, color: '#64748b', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><ChevronRight size={14} /></button>
        </div>
        {/* Dot indicators */}
        <div style={{ display: 'flex', gap: 4 }}>
          {ENGINES.map((_, i) => (
            <button key={i} onClick={() => advance(i)}
              style={{ width: i === idx ? 16 : 6, height: 6, borderRadius: 3, border: 'none', background: i === idx ? engine.color : '#1e293b', cursor: 'pointer', transition: 'width .3s, background .3s', padding: 0 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK LAUNCH GRID — 4 categories, pinnable favorites
// ═══════════════════════════════════════════════════════════════════════════════
const LAUNCH_CATEGORIES = [
  {
    label: 'Engineering', color: '#5eead4', icon: Ruler,
    items: [
      { id: 'r2-measurement-engine', label: 'Measurement',    icon: Ruler },
      { id: 'r2-3d-engine',          label: '3D Intelligence',icon: Layers },
      { id: 'r2-smart-viewer',       label: 'Smart Viewer',   icon: Cpu },
      { id: 'r2-dcre',               label: 'Disaster Sim',   icon: AlertTriangle },
      { id: 'r2-bwse',               label: 'BIM World',      icon: Building2 },
      { id: 'r2-iie',                label: 'Input Engine',   icon: Keyboard },
    ],
  },
  {
    label: 'AI', color: '#a78bfa', icon: Brain,
    items: [
      { id: 'r2-brain',        label: 'R2 Brain',      icon: Brain },
      { id: 'r2-voice-engine', label: 'Voice AI',      icon: Mic },
      { id: 'r2-role-agent',   label: 'Role Agents',   icon: Zap },
      { id: 'r2-ai-coach',     label: 'AI Coach',      icon: Star },
      { id: 'r2-calendar-engine', label: 'Calendar AI', icon: Clock },
      { id: 'r2-csie',         label: 'Scene Intel',   icon: Film },
    ],
  },
  {
    label: 'Creative', color: '#f472b6', icon: Film,
    items: [
      { id: 'r2-scse',   label: 'Strategy & Cinema', icon: Film },
      { id: 'r2-aiqe',   label: 'Audio Intel',       icon: Music },
      { id: 'r2-mvie',   label: 'Music Video AI',    icon: Camera },
      { id: 'r2-aie',    label: 'Asset Intel',       icon: Sparkles },
      { id: 'r2-ece',    label: 'Epic Cinema',       icon: Radio },
      { id: 'r2-wbe',    label: 'World Builder',     icon: Globe },
    ],
  },
  {
    label: 'Business', color: '#f59e0b', icon: TrendingUp,
    items: [
      { id: 'marketplace',        label: 'Marketplace',   icon: Package },
      { id: 'r2-finance-hub',     label: 'Finance Hub',   icon: TrendingUp },
      { id: 'investor-relations', label: 'Investors',     icon: Star },
      { id: 'r2-academy',         label: 'Academy',       icon: FileText },
      { id: 'r2-founder-command-center', label: 'Founder Console', icon: Zap },
      { id: 'global-command',     label: 'Global Command', icon: Globe },
    ],
  },
];

function QuickLaunchGrid({ onNavigate }: { onNavigate: (r: Route) => void }) {
  const STORAGE_KEY = 'r2:pinned-launches';
  const [pinned, setPinned] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); } catch { return []; }
  });

  const togglePin = (id: string) => {
    setPinned((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Quick Launch</h3>
        {pinned.length > 0 && (
          <div style={{ display: 'flex', gap: 6 }}>
            {pinned.map((pid) => {
              const item = LAUNCH_CATEGORIES.flatMap((c) => c.items).find((i) => i.id === pid);
              if (!item) return null;
              const Icon = item.icon;
              return (
                <button key={pid} data-testid={`pinned-${pid}`}
                  onClick={() => onNavigate(pid as Route)}
                  title={item.label}
                  style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid rgba(245,158,11,0.4)`, background: 'rgba(245,158,11,0.08)', color: AMBER, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                  <Icon size={13} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {LAUNCH_CATEGORIES.map((cat) => {
          const CatIcon = cat.icon;
          return (
            <div key={cat.label} style={{ background: PANEL, border: `1px solid ${BDR}`, borderRadius: 14, padding: 16, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                <CatIcon size={14} color={cat.color} />
                <span style={{ fontSize: 12, fontWeight: 700, color: cat.color }}>{cat.label}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {cat.items.map((item) => {
                  const ItemIcon = item.icon;
                  const isPinned = pinned.includes(item.id);
                  return (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <button data-testid={`launch-${item.id}`}
                        onClick={() => onNavigate(item.id as Route)}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', borderRadius: 7, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12, cursor: 'pointer', textAlign: 'left', transition: 'background .15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${cat.color}10`; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                        <ItemIcon size={12} />
                        {item.label}
                      </button>
                      <button onClick={() => togglePin(item.id)} title={isPinned ? 'Unpin' : 'Pin to top'}
                        style={{ width: 22, height: 22, borderRadius: 5, border: 'none', background: 'transparent', color: isPinned ? AMBER : '#2d3748', cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'color .15s' }}>
                        <Pin size={10} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTINUE WHERE YOU LEFT OFF
// ═══════════════════════════════════════════════════════════════════════════════
function ContinuePanel({ onNavigate }: { onNavigate: (r: Route) => void }) {
  const { snapshots } = useSessionResume();
  if (!snapshots.length) return null;

  return (
    <div data-testid="continue-panel" style={{ marginBottom: 20, background: PANEL, border: `1px solid ${BDR}`, borderRadius: 14, padding: '14px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Clock size={13} color={TEAL} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Continue Where You Left Off</span>
      </div>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto' }}>
        {snapshots.slice(0, 5).map((snap, i) => (
          <button key={i} data-testid={`resume-${snap.route}`}
            onClick={() => onNavigate(snap.route as Route)}
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 9,
              background: 'rgba(255,255,255,0.03)', border: `1px solid ${BDR}`, color: '#94a3b8', fontSize: 12, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}>
            <span style={{ fontSize: 16 }}>{snap.routeIcon}</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 500 }}>{snap.routeLabel}</div>
              <div style={{ fontSize: 10, color: '#475569' }}>{new Date(snap.timestamp).toLocaleDateString(undefined, { month:'short', day:'numeric' })}</div>
            </div>
            <ArrowRight size={11} style={{ marginLeft: 4 }} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE METRICS — Platform counters
// ═══════════════════════════════════════════════════════════════════════════════
function LiveMetrics() {
  const [iie, setIIE]     = useState<any>(null);
  const [health, setHealth] = useState<any>(null);

  useEffect(() => {
    fetch(`${API}/api/iie/stats`).then((r) => r.json()).then(setIIE).catch(() => {});
    fetch(`${API}/api/health`).then((r) => r.json()).then(setHealth).catch(() => {});
  }, []);

  const metrics = [
    { label: 'R2 Commands',  value: iie?.total_commands,  color: TEAL,   icon: Keyboard },
    { label: 'Input Profiles',value: iie?.total_profiles, color: '#a78bfa', icon: Layers },
    { label: 'Macro Scripts', value: iie?.total_macros,   color: AMBER,  icon: Activity },
    { label: 'AI Engines',    value: 12,                  color: '#f472b6', icon: Sparkles },
    { label: 'BIM Engines',   value: 8,                   color: '#60a5fa', icon: Building2 },
    { label: 'Platform Status',value: health?.status === 'ok' ? 'ONLINE' : 'CHECKING', color: '#4ade80', icon: Zap },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10, marginBottom: 20 }}>
      {metrics.map((m) => {
        const Icon = m.icon;
        return (
          <div key={m.label} data-testid={`metric-${m.label.replace(/\s+/g,'-').toLowerCase()}`}
            style={{ background: PANEL, border: `1px solid ${BDR}`, borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
            <Icon size={16} color={m.color} style={{ marginBottom: 6 }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>
              {m.value ?? '—'}
            </div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// WHAT'S NEW — Recent engine launches
// ═══════════════════════════════════════════════════════════════════════════════
const WHATS_NEW = [
  { label: 'R2 Input Intelligence Engine',    tag: 'NEW',    desc: 'Universal command mapper for every device',   color: TEAL   },
  { label: 'WSIE Stamp Mode',                 tag: 'UPDATE', desc: 'Click anywhere to stamp sticky notes',        color: '#fbbf24' },
  { label: 'Old Earth World Map',             tag: 'UPDATE', desc: '23 alternate civilizations · AI exploration', color: '#22d3ee' },
  { label: 'CATE Catastrophe Engine',         tag: 'UPDATE', desc: '18 disaster types with evacuation simulation',color: '#f87171' },
  { label: 'AIQE Audio Intelligence',         tag: 'BETA',   desc: 'BPM detection & auto-mastering analysis',     color: '#a78bfa' },
];

function WhatsNew({ onNavigate }: { onNavigate: (r: Route) => void }) {
  return (
    <div style={{ background: PANEL, border: `1px solid ${BDR}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Sparkles size={13} color={TEAL} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>What's New in R2</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {WHATS_NEW.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: i < WHATS_NEW.length - 1 ? `1px solid rgba(255,255,255,0.04)` : 'none' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: item.color, background: `${item.color}15`, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.06em', flexShrink: 0 }}>{item.tag}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 500 }}>{item.label}</span>
              <span style={{ fontSize: 11, color: '#475569', marginLeft: 8 }}>{item.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HEAT MAP PANEL — Inline (not just modal)
// ═══════════════════════════════════════════════════════════════════════════════
function HeatMapPanel({ expanded, onExpand }: { expanded: boolean; onExpand: () => void }) {
  const LAYERS = ['User Activity', 'Active Projects', 'AI Requests', 'Infrastructure', 'Disaster Zones'];
  const [activeLayers, setActiveLayers] = useState(['User Activity', 'Active Projects']);
  const toggleLayer = (l: string) => setActiveLayers((p) => p.includes(l) ? p.filter((x) => x !== l) : [...p, l]);

  return (
    <div style={{ background: PANEL, border: `1px solid ${BDR}`, borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: `1px solid ${BDR}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Map size={14} color={TEAL} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8' }}>Global Operations Map</span>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 2s infinite' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {LAYERS.map((l) => (
            <button key={l} onClick={() => toggleLayer(l)}
              style={{ padding: '3px 9px', borderRadius: 5, border: `1px solid ${activeLayers.includes(l) ? TEAL : BDR}`, background: activeLayers.includes(l) ? 'rgba(94,234,212,0.1)' : 'transparent', color: activeLayers.includes(l) ? TEAL : '#475569', fontSize: 10, cursor: 'pointer' }}>
              {l}
            </button>
          ))}
          <button data-testid="heatmap-expand-btn" onClick={onExpand}
            style={{ padding: '3px 9px', borderRadius: 5, border: `1px solid ${BDR}`, background: PANEL, color: '#64748b', fontSize: 10, cursor: 'pointer' }}>
            Full Screen
          </button>
        </div>
      </div>
      <div style={{ height: expanded ? 400 : 200 }}>
        <GlobalOperationalMap standalone />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDITABLE WIDGET WRAPPER — Shown in Founder Edit Mode
// ═══════════════════════════════════════════════════════════════════════════════
const SIZES: WidgetSize[] = ['compact', 'normal', 'large', 'hero'];
const SIZE_LABELS: Record<WidgetSize, string> = { compact: 'S', normal: 'M', large: 'L', hero: 'XL' };
const SIZE_TIPS:   Record<WidgetSize, string> = {
  compact: 'Compact — reduced padding',
  normal:  'Normal — default size',
  large:   'Large — extra padding',
  hero:    'Hero — max prominence',
};

function EditableWidget({ id, children }: { id: string; children: React.ReactNode }) {
  const config       = useWidget(id);
  const editMode     = useDashboardLayout((s) => s.editMode);
  const setSize      = useDashboardLayout((s) => s.setSize);
  const toggleVisible = useDashboardLayout((s) => s.toggleVisible);
  const moveUp       = useDashboardLayout((s) => s.moveUp);
  const moveDown     = useDashboardLayout((s) => s.moveDown);

  if (!config) return <>{children}</>;
  if (!config.visible && !editMode) return null;

  const { padding, scale } = SIZE_SCALE[config.size];

  return (
    <div
      data-testid={`widget-${id}`}
      style={{
        position: 'relative',
        padding,
        transform: scale,
        transformOrigin: 'top left',
        opacity: !config.visible ? 0.35 : 1,
        transition: 'padding .25s, opacity .25s, transform .25s',
        outline: editMode ? `2px dashed ${!config.visible ? '#334155' : 'rgba(94,234,212,0.25)'}` : 'none',
        outlineOffset: 4,
        borderRadius: 14,
      }}
    >
      {editMode && (
        <div
          data-testid={`widget-toolbar-${id}`}
          style={{
            position: 'absolute', top: -36, left: 0, right: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(6,10,20,0.95)',
            border: '1px solid rgba(94,234,212,0.25)',
            borderRadius: '9px 9px 0 0',
            padding: '5px 10px',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Label */}
          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', flex: 1, letterSpacing: '0.05em' }}>
            {config.label}
          </span>

          {/* Size buttons */}
          {SIZES.map((s) => (
            <button
              key={s}
              data-testid={`widget-size-${id}-${s}`}
              title={SIZE_TIPS[s]}
              onClick={() => setSize(id, s)}
              style={{
                width: 26, height: 22, borderRadius: 5, border: 'none', cursor: 'pointer',
                background: config.size === s ? 'rgba(94,234,212,0.2)' : 'rgba(255,255,255,0.04)',
                color: config.size === s ? '#5eead4' : '#475569',
                fontSize: 10, fontWeight: 700, transition: 'all .15s',
              }}
            >
              {SIZE_LABELS[s]}
            </button>
          ))}

          {/* Divider */}
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />

          {/* Move up/down */}
          <button data-testid={`widget-up-${id}`} onClick={() => moveUp(id)} title="Move up"
            style={{ width: 22, height: 22, borderRadius: 5, border: 'none', background: 'rgba(255,255,255,0.04)', color: '#475569', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <ChevronUp size={11} />
          </button>
          <button data-testid={`widget-down-${id}`} onClick={() => moveDown(id)} title="Move down"
            style={{ width: 22, height: 22, borderRadius: 5, border: 'none', background: 'rgba(255,255,255,0.04)', color: '#475569', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <ChevronDown size={11} />
          </button>

          {/* Divider */}
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.08)' }} />

          {/* Visibility toggle */}
          <button
            data-testid={`widget-toggle-${id}`}
            onClick={() => toggleVisible(id)}
            title={config.visible ? 'Hide widget' : 'Show widget'}
            style={{
              width: 26, height: 22, borderRadius: 5, border: 'none', cursor: 'pointer',
              background: config.visible ? 'rgba(255,255,255,0.04)' : 'rgba(248,113,113,0.15)',
              color: config.visible ? '#475569' : '#f87171',
              display: 'grid', placeItems: 'center', transition: 'all .15s',
            }}
          >
            {config.visible ? <Eye size={11} /> : <EyeOffIcon size={11} />}
          </button>
        </div>
      )}

      {children}
    </div>
  );
}

// ── Founder Save Bar ───────────────────────────────────────────────────────────
function FounderSaveBar() {
  const { editMode, setEditMode, hasChanges, saveLayout, resetLayout } = useDashboardLayout();

  if (!editMode) return null;

  return (
    <div
      data-testid="founder-save-bar"
      style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9000, display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(6,10,20,0.96)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(94,234,212,0.3)',
        borderRadius: 14, padding: '12px 20px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(94,234,212,0.1)',
        animation: 'slideUp .25s ease',
      }}
    >
      <Settings2 size={14} color="#5eead4" />
      <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginRight: 6 }}>
        Layout Editor
      </span>
      {hasChanges && (
        <span style={{ fontSize: 10, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '2px 7px', borderRadius: 4 }}>
          Unsaved changes
        </span>
      )}

      <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />

      <button
        data-testid="layout-reset-btn"
        onClick={() => { resetLayout(); toast.success('Layout reset to defaults'); }}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(248,113,113,0.3)', background: 'transparent', color: '#f87171', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
      >
        <RotateCcw size={11} /> Reset
      </button>

      <button
        data-testid="layout-cancel-btn"
        onClick={() => setEditMode(false)}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
      >
        <X size={11} /> Cancel
      </button>

      <button
        data-testid="layout-save-btn"
        onClick={() => { saveLayout(); setEditMode(false); toast.success('Dashboard layout saved!'); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8, border: '1px solid rgba(94,234,212,0.45)', background: 'linear-gradient(135deg, rgba(94,234,212,0.18) 0%, rgba(94,234,212,0.08) 100%)', color: '#5eead4', fontSize: 12, fontWeight: 800, cursor: 'pointer', boxShadow: '0 0 16px rgba(94,234,212,0.15)' }}
      >
        <Check size={13} /> Save Layout
      </button>
    </div>
  );
}


// ── Sub-components isolate local state ────────────────────────────────────────
function StatTilesSection() {
  const stats = useDashboardStats();
  const tilt  = useMouseParallax();
  return (
    <>
      {stats.isError && (
        <div data-testid="dashboard-error" className="mb-4 px-4 py-3 rounded-sm bg-red-500/10 border border-red-500/40 text-[12px] text-red-200">
          Failed to load dashboard stats. The platform service may be starting up.
        </div>
      )}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STATS.map((s, i) => {
          const Icon  = s.icon;
          const value = stats.data?.[s.field];
          const depth = 1.0 + (i % 4) * 0.15;
          const rx = -(tilt.y * 5.5 * depth).toFixed(3);
          const ry =  (tilt.x * 7.0 * depth).toFixed(3);
          const tz = (Math.abs(tilt.x) + Math.abs(tilt.y)) * 4 * depth;
          return (
            <div key={s.id} data-testid={`stat-${s.id}`} className="panel p-5 hover:border-zinc-700 transition-colors"
              style={{ transform: `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(${tz}px)`, transformStyle: 'preserve-3d', willChange: 'transform', boxShadow: `0 ${4 + Number(tz) * 0.5}px ${16 + Number(tz)}px rgba(37,181,168,${0.025 + Math.abs(tilt.x) * 0.04})`, transition: 'box-shadow 0.25s ease' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{s.label}</div>
                <Icon className="h-4 w-4 text-zinc-600" strokeWidth={1.5} />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold tracking-tight text-zinc-100">{stats.isLoading ? '…' : fmt(value)}</span>
              </div>
            </div>
          );
        })}
      </section>
      <DualWorldClock />
    </>
  );
}

function SystemCoreSection({ master }: { master: boolean }) {
  const [coreRevealed, setCoreRevealed] = useState(false);
  return (
    <section className="mt-2 mb-2">
      {master && coreRevealed ? (
        <div data-testid="revealed-core-wrap">
          <div className="flex justify-end mb-2">
            <button type="button" data-testid="conceal-core-btn" onClick={() => setCoreRevealed(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-[0.14em] transition"
              style={{ border: '1px solid rgba(45,181,168,0.5)', background: 'rgba(45,181,168,0.1)', color: '#5eead4' }}>
              <EyeOff className="h-3 w-3" /> Conceal Core
            </button>
          </div>
          <SystemOverviewDiagram />
        </div>
      ) : (
        <HolographicEngineCore isMaster={master} onReveal={() => setCoreRevealed(true)} />
      )}
    </section>
  );
}

function AuditTrailSection() {
  const stats    = useDashboardStats();
  const activity = useDashboardActivity(20);
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-2">
      <div className="panel p-6 lg:col-span-2" data-testid="event-stream">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Audit Trail</div>
            <h3 className="text-sm font-semibold mt-1">Recent platform activity</h3>
          </div>
          <span className="text-[11px] text-zinc-500 font-mono">{activity.data?.length ?? 0} entries</span>
        </div>
        <ul className="space-y-2 text-[12px] font-mono">
          {activity.isLoading && <li className="text-zinc-500 italic">Loading…</li>}
          {activity.data && activity.data.length === 0 && <li className="text-zinc-500 italic" data-testid="activity-empty">No platform activity recorded yet.</li>}
          {activity.data?.map((e, ix) => (
            <li key={ix} className="flex items-center gap-3 text-zinc-300">
              <span className="text-zinc-500 shrink-0">{new Date(e.timestamp).toLocaleTimeString([], { hour12: true })}</span>
              <span className={['shrink-0 px-1.5 py-0.5 text-[10px] uppercase rounded-sm', e.operation === 'INSERT' ? 'bg-emerald-500/15 text-emerald-300' : e.operation === 'UPDATE' ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300'].join(' ')}>{e.operation}</span>
              <span className="text-zinc-200 shrink-0">{e.tableName}</span>
              <span className="text-zinc-500 truncate">{e.recordId}</span>
              <span className="text-zinc-600 ml-auto truncate">{e.userEmail ?? 'system'}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="panel p-6" data-testid="cabling-graph-summary">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Cabling Graph</div>
        <h3 className="text-sm font-semibold mt-1">Port-level connectivity</h3>
        <div className="mt-5 space-y-3 text-[12px]">
          <Row label="Patch panels"   value={stats.data?.patchPanels} />
          <Row label="Switches"       value={stats.data?.switches} />
          <Row label="Outlets"        value={stats.data?.outlets} />
          <Row label="Ports"          value={stats.data?.ports} />
          <Row label="Connections"    value={stats.data?.connections} />
          <Row label="Fiber cables"   value={stats.data?.fiberCables} />
          <Row label="Copper cables"  value={stats.data?.copperCables} />
        </div>
        <div className="mt-6 pt-5 border-t border-zinc-800">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Schedule</div>
          <div data-testid="dashboard-calendar">
            <Calendar value={pickedDate} onChange={(v) => setPickedDate(v)} className="w-full" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
export function DashboardPage({ onNavigate }: { onNavigate?: (r: Route) => void } = {}): JSX.Element {
  const navigate  = onNavigate ?? (() => {});
  const { licenseStatus } = useLicense();
  const user = useAuthStore((s) => s.user);
  const userEmail = user?.email;
  const master    = Boolean(user?.role === 'Founder' || user?.roles?.includes('Founder'));
  const userName  = user?.name ?? user?.firstName ?? user?.email?.split('@')[0] ?? 'Engineer';
  const [showMap, setShowMap]         = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [ingestOpen, setIngestOpen]   = useState(false);

  // Layout store
  const { editMode, setEditMode, hasChanges } = useDashboardLayout();
  const sortedWidgets = useSortedWidgets();

  const showRcdd =
    licenseStatus?.status === 'verified' &&
    (licenseStatus.tier === 'contractor' || licenseStatus.tier === 'designer') &&
    !!licenseStatus.bicsiId;

  // Widget registry — every card/tile/section maps here
  const widgetMap: Record<string, React.ReactNode> = {
    'engine-of-day':  <EngineOfTheDay onNavigate={navigate} />,
    'continue':       <ContinuePanel onNavigate={navigate} />,
    'live-metrics':   <LiveMetrics />,
    'quick-launch':   <QuickLaunchGrid onNavigate={navigate} />,
    'whats-new':      <WhatsNew onNavigate={navigate} />,
    'heat-map':       <HeatMapPanel expanded={mapExpanded} onExpand={() => setShowMap(true)} />,
    'night-ops':      <NightOpsTileRow />,
    'submittal':      <SubmittalGenerator />,
    'context':        <MasterContextIngestor />,
    'stat-tiles':     <StatTilesSection />,
    'wins-feed':      <WinsFeed onNavigate={onNavigate ? (r: string) => onNavigate(r as Route) : undefined} />,
    'system-core':    <SystemCoreSection master={master} />,
    'founder-alloc':  master ? <FounderAllocationWidget onNavigate={navigate} /> : null,
    'audit-trail':    <AuditTrailSection />,
  };

  return (
    <div data-testid="dashboard-page" style={{ padding: '24px 32px', maxWidth: 1600, paddingBottom: editMode ? 120 : 32 }}>
      {onNavigate && <R2CommandCenter onNavigate={(r) => onNavigate(r)} />}
      <div className="mb-4"><CelebrationBanner /></div>
      <div className="mb-6"><EliteUnlock /></div>

      {/* ── Smart Ingest Banner ────────────────────────────────────────────── */}
      <button
        data-testid="dashboard-smart-ingest-btn"
        onClick={() => setIngestOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 16, width: '100%',
          marginBottom: 20, padding: '16px 24px', borderRadius: 10,
          background: 'linear-gradient(135deg, rgba(37,181,168,0.12) 0%, rgba(245,158,11,0.10) 100%)',
          border: '1px solid rgba(37,181,168,0.35)', cursor: 'pointer',
          textAlign: 'left', transition: 'border-color 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(37,181,168,0.7)')}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(37,181,168,0.35)')}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 10, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(37,181,168,0.15)', border: '1px solid rgba(37,181,168,0.4)',
        }}>
          <Wand2 size={22} style={{ color: '#5eead4' }} strokeWidth={1.5} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 3, letterSpacing: '-0.01em' }}>
            Smart Ingest
          </div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
            Paste emails, drop files, or select a folder — R2 extracts contacts, project codes, action items, and more in seconds
          </div>
        </div>
        <div style={{
          flexShrink: 0, padding: '8px 18px', borderRadius: 7,
          background: 'rgba(37,181,168,0.15)', border: '1px solid rgba(37,181,168,0.4)',
          fontSize: 11, fontWeight: 700, color: '#5eead4', letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Open Ingest
        </div>
      </button>

      <QuickIngestDialog open={ingestOpen} onClose={() => setIngestOpen(false)} onNavigate={(r) => navigate(r as Route)} />

      {/* ── Mission Control Header ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.22em', color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>R2 Mission Control</div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: '#f1f5f9', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Welcome back, {userName.split(' ')[0]}
          </h1>
          <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>
            AI-Powered Engineering Operating System · {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {showRcdd && licenseStatus && (
            <RcddBadge bicsiId={licenseStatus.bicsiId!} tier={licenseStatus.tier as 'contractor' | 'designer'} variant="card" />
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <DemoModeToggle />
            {onNavigate && (
              <button data-testid="dashboard-folder-monitor-shortcut" onClick={() => onNavigate('sync')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 11, borderRadius: 7, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: AMBER, cursor: 'pointer' }}>
                <FolderSearch size={13} /> Monitor Folder
              </button>
            )}
            {/* ── Founder: Customize Layout ─────────────────────────────── */}
            {master && (
              <button
                data-testid="customize-layout-btn"
                onClick={() => setEditMode(!editMode)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer', transition: 'all .2s',
                  border: editMode ? '1px solid rgba(94,234,212,0.5)' : '1px solid rgba(167,139,250,0.4)',
                  background: editMode ? 'rgba(94,234,212,0.1)' : 'rgba(167,139,250,0.08)',
                  color: editMode ? '#5eead4' : '#c4b5fd',
                  boxShadow: editMode ? '0 0 16px rgba(94,234,212,0.15)' : '0 0 10px rgba(167,139,250,0.1)',
                }}
              >
                <Settings2 size={13} />
                {editMode ? 'Editing Layout…' : 'Customize Layout'}
                {hasChanges && (
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: AMBER, flexShrink: 0 }} />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit Mode instruction banner ──────────────────────────────────── */}
      {editMode && (
        <div style={{ marginBottom: 24, padding: '12px 18px', background: 'rgba(94,234,212,0.05)', border: '1px solid rgba(94,234,212,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Settings2 size={13} color="#5eead4" />
          <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
            <span style={{ color: '#5eead4', fontWeight: 700 }}>Layout Editor active — every section is now editable.</span>
            {' '}Hover any widget to reveal its toolbar.
            Use <kbd style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontSize: 10, margin: '0 2px' }}>S</kbd>
            <kbd style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontSize: 10, margin: '0 2px' }}>M</kbd>
            <kbd style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontSize: 10, margin: '0 2px' }}>L</kbd>
            <kbd style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontSize: 10, margin: '0 2px' }}>XL</kbd>
            {' '}to resize · <kbd style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontSize: 10, margin: '0 2px' }}>↑↓</kbd> to reorder · eye icon to hide.
            Hit <strong style={{ color: '#5eead4' }}>Save Layout</strong> below to persist.
          </span>
        </div>
      )}

      {/* ── All widgets — sorted by layout order, each wrapped ────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: editMode ? 32 : 0 }}>
        {sortedWidgets.map((w) => {
          const node = widgetMap[w.id];
          // skip founder widget for non-masters
          if (w.id === 'founder-alloc' && !master) return null;
          // skip null nodes
          if (node === null || node === undefined) return null;
          return (
            <EditableWidget key={w.id} id={w.id}>
              {node}
            </EditableWidget>
          );
        })}
      </div>

      <IntelligenceFeed />
      <footer className="mt-8 mb-4"><CommunityPartners /></footer>

      {/* ── Global Map Full Overlay ───────────────────────────────────────── */}
      {showMap && (
        <div data-testid="dashboard-global-map-overlay"
          className="fixed inset-0 z-[120] flex flex-col p-4 md:p-8"
          style={{ background: 'rgba(4,7,13,0.96)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowMap(false)}>
          <div className="flex items-center gap-3 mb-4 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Globe className="h-5 w-5 text-teal-300" />
            <div className="flex flex-col">
              <span className="text-white font-semibold tracking-wide text-sm">RCD² Engine · Global Operations</span>
              <span className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Live Network</span>
            </div>
            <span className="ml-auto flex items-center gap-2 text-[11px] text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Live
            </span>
            <button type="button" data-testid="dashboard-global-map-close" onClick={() => setShowMap(false)}
              className="h-9 w-9 grid place-items-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-white transition">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
            <div className="h-full max-w-[1500px] mx-auto flex flex-col">
              <GlobalOperationalMap standalone />
            </div>
          </div>
        </div>
      )}

      {/* ── Floating Save Bar ────────────────────────────────────────────── */}
      <FounderSaveBar />

      <style>{`
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes slideUp { from{opacity:0;transform:translateX(-50%) translateY(16px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
      `}</style>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number | undefined }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className="font-mono text-zinc-100">{fmt(value)}</span>
    </div>
  );
}

// ── Founder Allocation Widget ──────────────────────────────────────────────────
function FounderAllocationWidget({ onNavigate }: { onNavigate: (r: Route) => void }): JSX.Element {
  const [metrics, setMetrics] = useState<{ reserved_pct:number; under_discussion_pct:number; available_pct:number; current_tier_label:string; current_tier_status:string } | null>(null);
  useEffect(() => {
    fetch('/api/investor/public').then(r => r.json()).then((d: any) => setMetrics(d.metrics)).catch(() => {});
  }, []);
  return (
    <div data-testid="founder-allocation-widget"
      style={{ background:'rgba(6,12,26,0.8)', border:'1px solid rgba(45,181,168,0.18)', borderRadius:16, padding:'20px 24px', marginBottom:4, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        <div style={{ width:36, height:36, borderRadius:10, display:'grid', placeItems:'center', background:'rgba(45,181,168,0.1)', border:'1px solid rgba(45,181,168,0.25)' }}>
          <TrendingUp size={16} style={{ color:'#5eead4' }} />
        </div>
        <div>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.18em', textTransform:'uppercase', color:'rgba(45,181,168,0.55)', marginBottom:2 }}>Founder Allocation</div>
          <div style={{ fontSize:12, fontWeight:800, color:'#e2e8f0' }}>Investment Window Tracker</div>
        </div>
      </div>
      {metrics ? (
        <div style={{ display:'flex', gap:16, flex:1, flexWrap:'wrap' }}>
          {[{label:'Reserved',val:metrics.reserved_pct,color:'#f87171'},{label:'Under Discussion',val:metrics.under_discussion_pct,color:'#fbbf24'},{label:'Available',val:metrics.available_pct,color:'#4ade80'}].map(m => (
            <div key={m.label} style={{ textAlign:'center', minWidth:80 }}>
              <div style={{ fontSize:20, fontWeight:900, color:m.color }}>{m.val}%</div>
              <div style={{ fontSize:9, color:'rgba(148,172,210,0.45)', textTransform:'uppercase', letterSpacing:'0.1em' }}>{m.label}</div>
            </div>
          ))}
          <div style={{ textAlign:'center', minWidth:100 }}>
            <div style={{ fontSize:11, fontWeight:700, color:metrics.current_tier_status==='Active'?'#4ade80':'#f87171' }}>{metrics.current_tier_status}</div>
            <div style={{ fontSize:9, color:'rgba(148,172,210,0.45)', textTransform:'uppercase', letterSpacing:'0.1em' }}>{metrics.current_tier_label}</div>
          </div>
        </div>
      ) : <div style={{ fontSize:11, color:'rgba(148,172,210,0.3)', flex:1 }}>Loading…</div>}
      <button data-testid="founder-widget-cta" onClick={() => onNavigate('investor-relations')}
        style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0, background:'rgba(45,181,168,0.08)', border:'1px solid rgba(45,181,168,0.25)', borderRadius:8, padding:'8px 14px', color:'#5eead4', fontSize:11, fontWeight:700, cursor:'pointer' }}>
        Manage <ChevronRight size={11} />
      </button>
    </div>
  );
}
